import { useEffect, useMemo, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text, Icon, iconWeight, Button } from '@nula/core-ui';
import { EventRow } from './event-row';
import { useCalendarStore } from '@/stores/use-calendar-store';
import type { CalendarEvent } from '@/stores/use-calendar-store';
import { getCalendarColor } from '@/lib/calendar/calendar-colors';
import { openCalendarOAuth } from '@/lib/calendar/calendar-oauth';
import { localDateKey } from '@/lib/calendar/date-utils';
import { openExternalUrl } from '@/utils/open-external-url';
import styles from './home-calendar-widget.module.css';

const homeWidget = cva(styles.base);

type Range = 'today' | 'week' | 'month';

interface HomeCalendarWidgetProps {
  onEventClick?: (event: CalendarEvent) => void;
  /** YYYY-MM-DD date selected in the mini calendar — overrides the range filter */
  focusedDate?: string | null;
}

function getWeekBounds(anchor: Date): { start: Date; end: Date } {
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - anchor.getDay()); // Sunday
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function getMonthBounds(anchor: Date): { start: Date; end: Date } {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

function fmtTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtDayHeader(dateKey: string): string {
  const d = new Date(dateKey + 'T12:00:00');
  return new Intl.DateTimeFormat(navigator.language, { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
}

function isTodayKey(dateKey: string): boolean {
  return dateKey === localDateKey(new Date());
}

export function HomeCalendarWidget({ onEventClick, focusedDate }: HomeCalendarWidgetProps) {
  const { connections, events, fetchConnections, fetchEvents, selectEvent, subscriptions } = useCalendarStore();
  const [range, setRange] = useState<Range>('today');
  const now = useMemo(() => new Date(), []);
  const todayKey = localDateKey(now);

  // When a date is picked from the mini calendar, derive which range tab it maps to
  // and sync the tab automatically
  useEffect(() => {
    if (!focusedDate) return;
    if (focusedDate === todayKey) {
      setRange('today');
    } else {
      // If it's in the same week as today → week; otherwise → month
      const { start: ws, end: we } = getWeekBounds(now);
      const focused = new Date(focusedDate + 'T12:00:00');
      if (focused >= ws && focused <= we) {
        setRange('week');
      } else {
        setRange('month');
      }
    }
  }, [focusedDate, todayKey, now]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Anchor for fetching: use focusedDate when set, otherwise today
  const anchor = useMemo(() => {
    if (focusedDate) return new Date(focusedDate + 'T12:00:00');
    return now;
  }, [focusedDate, now]);

  useEffect(() => {
    if (connections.length === 0) return;

    if (range === 'today') {
      const start = new Date(anchor);
      start.setHours(0, 0, 0, 0);
      const end = new Date(anchor);
      end.setHours(23, 59, 59, 999);
      fetchEvents(start, end);
    } else if (range === 'week') {
      const { start, end } = getWeekBounds(anchor);
      fetchEvents(start, end);
    } else {
      const { start, end } = getMonthBounds(anchor);
      fetchEvents(start, end);
    }
  }, [connections.length, fetchEvents, range, anchor]);

  const groupedDays = useMemo((): Array<{ dateKey: string; dayEvents: CalendarEvent[] }> => {
    if (range === 'today') {
      // Use focusedDate if set, else today
      const key = focusedDate ?? todayKey;
      const dayEvents = (events[key] ?? []).sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      );
      if (dayEvents.length === 0) return [];
      return [{ dateKey: key, dayEvents }];
    }

    const { start, end } = range === 'week' ? getWeekBounds(anchor) : getMonthBounds(anchor);
    const startKey = localDateKey(start);
    const endKey = localDateKey(end);

    return Object.entries(events)
      .filter(([k]) => k >= startKey && k <= endKey)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, dayEvents]) => ({
        dateKey,
        dayEvents: dayEvents.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
      }))
      .filter(({ dayEvents }) => dayEvents.length > 0);
  }, [events, range, todayKey, anchor, focusedDate]);

  const handleEventClick = (event: CalendarEvent) => {
    selectEvent(event);
    onEventClick?.(event);
  };

  if (connections.length === 0) {
    return (
      <Flex vertical align="center" justify="center" gap="sm" className={styles.emptyState}>
        <span className={styles.emptyIcon}>
          <Icon name="calendar_month" size={24} weight={iconWeight.light} />
        </span>
        <Text className={styles.emptyTitle}>Connect your calendar</Text>
        <Text type="secondary" className={styles.emptyDesc}>
          See your meetings here and let Nula prep notes and capture them automatically.
        </Text>
        <Button
          size="sm"
          variant="primary"
          icon={<Icon name="calendar_month" size={15} weight={iconWeight.light} />}
          onClick={async () => {
            try {
              await openCalendarOAuth('google');
            } catch {}
          }}
          className={styles.emptyButton}
        >
          Connect calendar
        </Button>
      </Flex>
    );
  }

  const TABS: { key: Range; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
  ];

  const emptyLabel = range === 'today'
    ? (focusedDate && focusedDate !== todayKey ? 'No events that day' : 'No events today')
    : range === 'week' ? 'No events this week'
    : 'No events this month';

  return (
    <Flex vertical gap="lg" className={homeWidget()}>
      {/* Range selector */}
      <Flex align="center" gap="xs" className={styles.tabsRow}>
        {TABS.map(tab => (
          <Flex
            key={tab.key}
            align="center"
            justify="center"
            onClick={() => setRange(tab.key)}
            className={styles.tab}
          >
            <Text
              size="xs"
              className={clsx(
                styles.tabLabel,
                range === tab.key ? styles.tabLabelActive : styles.tabLabelInactive,
              )}
            >
              {tab.label}
            </Text>
          </Flex>
        ))}
      </Flex>

      {/* Event list */}
      <Flex vertical gap="md" className={styles.eventList}>
        {groupedDays.length === 0 ? (
          <Flex vertical align="center" justify="center" gap="sm" className={styles.emptyState}>
            <span className={styles.emptyIcon}>
              <Icon name="event_available" size={24} weight={iconWeight.light} />
            </span>
            <Text className={styles.emptyTitle}>{emptyLabel}</Text>
            <Text type="secondary" className={styles.emptyDesc}>
              Enjoy the focus. Your upcoming events will show up here.
            </Text>
          </Flex>
        ) : (
          groupedDays.map(({ dateKey, dayEvents }) => (
            <Flex key={dateKey} vertical gap="xs">
              {/* Day header — only shown in week/month range */}
              {range !== 'today' && (
                <Text
                  size="xs"
                  className={clsx(
                    styles.dayHeader,
                    isTodayKey(dateKey) ? styles.dayHeaderToday : styles.dayHeaderDefault,
                  )}
                >
                  {isTodayKey(dateKey) ? 'Today' : fmtDayHeader(dateKey)}
                </Text>
              )}

              {dayEvents.map(event => {
                const start = new Date(event.startAt);
                const end = new Date(event.endAt);
                const color = getCalendarColor(event.subscriptionId, subscriptions[event.subscriptionId]?.color);
                const isNow = !event.isAllDay && start <= now && end > now;
                const timeLabel = isNow
                  ? `Now · until ${fmtTime(end)}`
                  : event.isAllDay ? 'All day' : fmtTime(start);

                return (
                  <EventRow
                    key={event.id}
                    time={timeLabel}
                    title={event.title}
                    color={color}
                    isNow={isNow}
                    icon={event.linkedDocumentIds?.length > 0
                      ? <Icon name="description" size={12} weight={iconWeight.light} className={styles.linkedIcon} />
                      : undefined
                    }
                    onClick={() => handleEventClick(event)}
                    onJoin={event.meetLink ? () => { void openExternalUrl(event.meetLink!); } : undefined}
                  />
                );
              })}
            </Flex>
          ))
        )}
      </Flex>
    </Flex>
  );
}
